import React from 'react';
import classNames from 'classnames';

import defaultFooter from '../../../../config/defaultFooter.json';
import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage } from '../../../../util/reactIntl';

import wedlyLogo from '../../../../assets/wedly-logo.png';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

const GRID_CONFIG = [
  { gridCss: css.gridCol1 },
  { gridCss: css.gridCol2 },
  { gridCss: css.gridCol3 },
  { gridCss: css.gridCol4 },
];
const MAX_COLS = 4;

const getGridCss = numberOfColumns => {
  const idx = Math.min(Math.max(numberOfColumns, 1), MAX_COLS) - 1;
  return GRID_CONFIG[idx]?.gridCss || GRID_CONFIG[0].gridCss;
};

/**
 * Bundled default matches `public/content/design` footer asset shape. Used when the hosted
 * footer asset is missing or has no link blocks and no social links (e.g. local dev).
 *
 * @param {Object} props - Section props from CMS / mergeConfig
 * @returns {{ blocks: Array, socialMediaLinks: Array, numberOfColumns: number }}
 */
const getFooterContentFromProps = props => {
  const blocks = props.blocks ?? [];
  const socialMediaLinks = props.socialMediaLinks ?? [];
  const hasBlocks = Array.isArray(blocks) && blocks.length > 0;
  const hasSocial = Array.isArray(socialMediaLinks) && socialMediaLinks.length > 0;

  if (hasBlocks && hasSocial) {
    return {
      blocks,
      socialMediaLinks,
      numberOfColumns:
        props.numberOfColumns ?? defaultFooter.numberOfColumns ?? MAX_COLS,
    };
  }

  return {
    blocks: defaultFooter.blocks ?? [],
    socialMediaLinks: defaultFooter.socialMediaLinks ?? [],
    numberOfColumns: defaultFooter.numberOfColumns ?? MAX_COLS,
  };
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Footer section: multi-column link blocks, optional social column, divider, copyright.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for link blocks (1–4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan (legacy CMS field; not shown in current layout)
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    copyright,
    appearance,
    options,
  } = props;

  const { blocks, socialMediaLinks, numberOfColumns } = getFooterContentFromProps(props);

  const config = useConfiguration();
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks.map((sml, i) => ({
    ...sml,
    blockType: sml.blockType || 'socialMediaLink',
    blockId: sml.link?.platform || sml.blockId || `social-${i}`,
  }));

  const showSocialMediaLinks = linksWithBlockId.length > 0;
  const hasLinkBlocks = blocks.length > 0;
  const showCopyrightFromCms = copyright && hasDataInFields([copyright], fieldOptions);

  const linksGridClass = classNames(css.linkColumns, getGridCss(numberOfColumns));

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
      contentClassName={css.footerSectionContent}
    >
      <div className={css.footerInner}>
        <div className={css.logoBackground} aria-hidden="true">
          <img className={css.logoBackgroundMark} src={wedlyLogo} alt="" />
        </div>

        <div className={css.footerContent}>
          <div className={css.top}>
            <div className={css.navGrid}>
            {hasLinkBlocks ? (
              <div className={linksGridClass}>
                <BlockBuilder
                  blocks={blocks}
                  sectionId={sectionId}
                  options={options}
                  textClassName={css.footerBlockText}
                />
              </div>
            ) : null}

            {showSocialMediaLinks ? (
              <div className={css.socialColumn}>
                <h2 className={css.columnHeading}>
                  <FormattedMessage id="Footer.followUs" />
                </h2>
                <div className={css.socialIcons}>
                  <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
                </div>
              </div>
            ) : null}
            </div>
          </div>

          <hr className={css.divider} />

          <div className={css.bottom}>
          {showCopyrightFromCms ? (
            <Field data={copyright} className={css.copyright} options={fieldOptions} />
          ) : (
            <p className={css.copyright}>
              <FormattedMessage
                id="Footer.defaultCopyright"
                values={{
                  year: new Date().getFullYear(),
                  marketplaceName: config.marketplaceName,
                }}
              />
            </p>
          )}
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
